import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentActivityState } from '../hooks/useAgentAsk';
import '../styles/agent-activity.css';
import { Icon } from './shared/Icon';

const NARRATION_MAX = 220;

function truncate(text: string, max: number): string {
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function StepRow({
  step,
}: {
  step: AgentActivityState['steps'][number];
}) {
  return (
    <li className={`agent-step agent-step--${step.state}`}>
      <span className="agent-step-icon" aria-hidden>
        {step.state === 'done' ? (
          <Icon name="check" size={12} stroke={2.6} />
        ) : (
          <span className="agent-step-spinner" />
        )}
      </span>
      <span className="agent-step-body">
        <span className="agent-step-label">{step.label}</span>
        {step.detail && (
          <span className="agent-step-detail">{step.detail}</span>
        )}
      </span>
    </li>
  );
}

export function AgentActivity({ activity }: { activity: AgentActivityState }) {
  const { t } = useTranslation('assistant');
  const { statusMessage, narration, composing, steps, draft } = activity;
  const [open, setOpen] = useState(false);
  const stepKey = steps.map(step => step.stepId).join('|');

  useEffect(() => {
    if (steps.length === 0) setOpen(false);
  }, [stepKey, steps.length]);

  const newestFirst = [...steps].reverse();
  const latest = newestFirst[0];
  const allStepsDone =
    steps.length > 0 && steps.every(step => step.state === 'done');
  const writing = (composing || allStepsDone) && !draft;
  const headline = writing
    ? t('writing')
    : latest?.label ??
      statusMessage ??
      (steps.length > 0 ? t('gathering') : t('working'));
  const canExpand = steps.length > 1;

  return (
    <div className="agent-activity" role="status" aria-live="polite">
      <div className="agent-activity-card">
        <div className="agent-activity-head">
          <span className="agent-activity-spin">
            <Icon name="activity" size={14} stroke={2.4} />
          </span>
          <div className="agent-activity-lead">
            <span>{headline}</span>
            {!writing && latest?.detail && !open && (
              <span className="agent-step-detail">{latest.detail}</span>
            )}
          </div>
          {canExpand && (
            <button
              type="button"
              className={`agent-activity-toggle${open ? ' is-open' : ''}`}
              aria-expanded={open}
              onClick={() => setOpen(v => !v)}
            >
              {t('steps', { count: steps.length })}
              <Icon name="chevron" size={12} stroke={2.4} />
            </button>
          )}
        </div>

        {narration && (
          <p className="agent-activity-narration">
            {truncate(narration, NARRATION_MAX)}
          </p>
        )}

        {open && canExpand && (
          <ol className="agent-activity-steps">
            {newestFirst.map(step => (
              <StepRow key={step.stepId} step={step} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
